import { API_PATCH, API_PUT, DBRequest } from './api';

/**
 * Syncs HOAI positions from ExoPositions back to the HOAI backend.
 * Call this before document submission to persist any position changes
 * (title edits, value changes, deletions) made directly in ExoPositions.
 *
 * @param {Object} params
 * @param {Object} params.config - API config from useConfig()
 * @param {Array} params.positions - All ExoPositions items (dataCollection.positions)
 * @returns {Promise<Array>} - Array of API responses for each updated HOAI
 */
export async function syncHoaiPositions({ config, positions }) {
  if (!config || !positions || positions.length === 0) return [];

  // Group ExoPositions items by hoaiId
  const hoaiPositionMap = {};
  positions.forEach((pos) => {
    if (!pos.hoaiId) return;
    if (!hoaiPositionMap[pos.hoaiId]) {
      hoaiPositionMap[pos.hoaiId] = [];
    }
    hoaiPositionMap[pos.hoaiId].push(pos);
  });

  const promises = [];

  for (const hoaiId of Object.keys(hoaiPositionMap)) {
    const exoItems = hoaiPositionMap[hoaiId];
    if (!exoItems || exoItems.length === 0) continue;

    const payload = buildHoaiPayload(exoItems);
    if (!payload) continue;

    promises.push(
      DBRequest({
        config,
        path: `hoai/${hoaiId}/sync-positions`,
        method: API_PATCH(payload),
      }),
    );
  }

  return Promise.all(promises);
}

function buildHoaiPayload(exoItems) {
  const phaseItems = exoItems.filter((p) => p.hoaiType === 'phase');
  const siemonItems = exoItems.filter((p) => p.hoaiType === 'siemonPhase');

  // Build positions from ExoPositions items
  const positions = phaseItems.map((item) => {
    const phaseSiemons = siemonItems.filter(
      (s) => s.phaseNumber === item.phaseNumber,
    );

    const netto = Number(item.price || 0) * Number(item.amount || 0);
    const position = {
      ...(item.hoaiPosition?.id != null && { id: item.hoaiPosition.id }),
      title: item.name,
      value:
        item.amount != null
          ? Math.round(Number(item.amount) * 100 * 100) / 100
          : undefined,
      phase_honorarium: Math.round(netto * 100) / 100,
      ...((item.isFlat || item.hoaiPosition?.type === 'flat') && {
        type: 'flat',
      }),
      phase_number: item.phaseNumber,
      description: item.description,
    };

    if (phaseSiemons.length > 0) {
      position.siemon_positions = phaseSiemons.map((s, sIndex) => ({
        ...(s.hoaiSiemonPosition?.id != null && {
          id: s.hoaiSiemonPosition.id,
        }),
        title: s.name,
        value:
          s.amount != null
            ? Math.round(Number(s.amount) * 100 * 100) / 100
            : undefined,
        description: s.description,
        phase_number: sIndex + 1,
      }));
    }

    return position;
  });

  // Extract additional costs
  const find = (type) => exoItems.find((p) => p.hoaiType === type);
  const surchargePos = find('surcharge');
  const timeFeePos = find('timeFee');
  const specialServicesPos = find('specialServices');
  const additionalServicesPos = find('additionalServices');
  const incidentalCostsPos = find('incidentalCosts');

  return {
    positions,
    ...(surchargePos && {
      surcharge: Number(surchargePos.amount) * 100,
      surcharge_title: surchargePos.name,
      surcharge_description: surchargePos.description,
    }),
    ...(timeFeePos && {
      time_fee: Number(timeFeePos.price),
      time_fee_title: timeFeePos.name,
      time_fee_description: timeFeePos.description,
    }),
    ...(specialServicesPos && {
      special_services: Number(specialServicesPos.price),
      special_services_title: specialServicesPos.name,
      special_services_description: specialServicesPos.description,
    }),
    ...(additionalServicesPos && {
      additional_services: Number(additionalServicesPos.price),
      additional_services_title: additionalServicesPos.name,
      additional_services_description: additionalServicesPos.description,
    }),
    ...(incidentalCostsPos && {
      incidental_costs: Number(incidentalCostsPos.amount) * 100,
      incidental_costs_title: incidentalCostsPos.name,
      incidental_costs_description: incidentalCostsPos.description,
    }),
  };
}
